import { useMemo, useState } from 'react'
import { type Resolver } from 'react-hook-form'
import { optionalNumber } from '@/components/add-property-dialog/schema'
import { useAccountCurrency } from '@/lib/use-account-currency'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import { z } from 'zod'
import {
	Alert, AlertDescription, AlertTitle,
	Button,
	ConfirmDialog,
	Form,
	Sheet, SheetContent,
} from '@rentflowdev/ui'
import { extractErrorMessage } from '@/lib/error-message'
import { useCloseGuard } from '@/lib/use-close-guard'
import {
	useUpsertRentTermsMutation,
	useUpsertPropertyUtilityMutation,
	useDeletePropertyUtilityMutation,
	useCreateAdditionalServiceMutation,
	useUpdateAdditionalServiceMutation,
	useDeleteAdditionalServiceMutation,
} from '@/api'
import { Step3RentTerms } from '@/components/add-property-dialog/step-3-rent-terms'
import { Step4Utilities }   from '@/components/add-property-dialog/step-4-utilities'
import { Step5AdditionalServices } from '@/components/add-property-dialog/step-5-additional-services'
import { usePersistedForm } from '@/lib/use-persisted-form'
import type { CurrencyCode, Property, PropertyUtility, PropertyAdditionalService, RentPeriod, UtilityType } from '@/types'
import { CURRENCIES, DEFAULT_CURRENCY } from '@/types'

type Currency = CurrencyCode
const asCurrency = (s?: string): Currency =>
	(s && (CURRENCIES as readonly string[]).includes(s)) ? s as Currency : DEFAULT_CURRENCY

const utilityEntrySchema = z.object({
	calculation:      z.enum(['BY_METER', 'PROPORTIONAL', 'FIXED_PER_SQM', 'FIXED_AMOUNT', 'COEFFICIENT', 'INCLUDED']).optional(),
	paidBy:           z.enum(['TENANT', 'LANDLORD']).optional(),
	hasSeparateMeter: z.boolean().optional(),
	meterNumber:      z.string().optional(),
	propertyArea:     optionalNumber(),
	buildingArea:     optionalNumber(),
	coefficient:      optionalNumber(),
	ratePerSqm:       optionalNumber(),
	fixedAmount:      optionalNumber(),
	currency:         z.enum(['RUB', 'USD', 'EUR']).optional(),
})

const additionalServiceSchema = z.object({
	id:           z.string().optional(),
	name:         z.string().trim().optional(),
	pricePerUnit: optionalNumber(),
	currency:     z.enum(['RUB', 'USD', 'EUR']).optional(),
	quantity:     optionalNumber(),
	unit:         z.string().trim().optional(),
	provisionType: z.enum(['OWN', 'AGENCY']).optional(),
	vatIncluded:  z.boolean().optional(),
})

const rentSheetSchema = z.object({
	rentAmount:        optionalNumber(),
	periodicity:       z.enum(['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY']).optional(),
	currency:          z.enum(['RUB', 'USD', 'EUR']).optional(),
	// Ставка НДС выводится на сервере из налогового режима; форма решает
	// только, входит ли она в цену.
	vatIncluded:       z.boolean().optional(),
	depositAmount:     optionalNumber(),
	penaltyPercent:    optionalNumber(),
	penaltyGraceDays:  optionalNumber(z.coerce.number().int()),
	// Utilities on top of the rent as one sum — a `PropertyRentTerms` column,
	// not a PropertyUtility row: that one answers who pays, this one how much.
	otherUtilitiesPayment: optionalNumber(),

	// ── Коммерческая аренда ───────────────────────────────────────────────
	// Step3RentTerms рисует их по типу объекта; без объявления здесь zod
	// вырезал бы значения при сохранении — как это было в листе правки.
	rentUnit:                  z.enum(['PER_MONTH', 'PER_SQM_MONTH', 'PER_YEAR', 'PER_SQM_YEAR', 'PER_SEAT_MONTH']).optional(),
	agentCommissionPercent:    z.coerce.number().min(0).max(100).optional(),
	leaseKind:                 z.enum(['DIRECT', 'SUBLEASE']).optional(),
	rentHolidays:              z.boolean().optional(),
	minLeaseMonths:            z.coerce.number().int().min(1).optional(),
	utilitiesIncluded:         z.boolean().optional(),
	operatingExpensesIncluded: z.boolean().optional(),
	// Месяцы аренды числом: залог бывает и в 12 месяцев, перечисление доходило
	// до трёх. Половины сохраняются — о них тоже договариваются.
	depositMonths:             optionalNumber(z.coerce.number().min(0).max(999)),

	utilities: z.object({
		ELECTRICITY: utilityEntrySchema,
		WATER:       utilityEntrySchema,
		HEATING:     utilityEntrySchema,
		WASTE:       utilityEntrySchema,
	}),
	additionalServices: z.array(additionalServiceSchema).optional(),
}).superRefine((values, ctx) => {
	// Как и в мастере: rent_amount не допускает пустого значения, поэтому без
	// ставки весь блок условий не отправлялся и залог с пеней пропадали молча.
	const hasRentTerm =
		values.depositAmount != null
		|| values.penaltyPercent != null
		|| values.penaltyGraceDays != null
		|| values.otherUtilitiesPayment != null
		|| values.agentCommissionPercent != null
		|| values.minLeaseMonths != null
		|| values.rentUnit != null
		|| values.leaseKind != null
		|| values.depositMonths != null
	if (hasRentTerm && !(values.rentAmount != null && values.rentAmount > 0)) {
		ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rentAmount'], message: 'Укажите арендную ставку — без неё условия аренды не сохранятся' })
	}
})

type RentSheetValues = z.infer<typeof rentSheetSchema>

const UTILITY_TYPES = ['ELECTRICITY', 'WATER', 'HEATING', 'WASTE'] as const

const utilityToForm = (u?: PropertyUtility) => ({
	calculation:      u?.calculation,
	paidBy:           u?.paidBy,
	hasSeparateMeter: u?.hasSeparateMeter,
	meterNumber:      u?.meterNumber,
	propertyArea:     u?.propertyArea  != null ? Number(u.propertyArea)  : undefined,
	buildingArea:     u?.buildingArea  != null ? Number(u.buildingArea)  : undefined,
	coefficient:      u?.coefficient   != null ? Number(u.coefficient)   : undefined,
	ratePerSqm:       u?.ratePerSqm    != null ? Number(u.ratePerSqm)    : undefined,
	fixedAmount:      u?.fixedAmount   != null ? Number(u.fixedAmount)   : undefined,
	currency:         asCurrency(u?.currency),
})

const serviceToForm = (s: PropertyAdditionalService) => ({
	id:           s.id,
	name:         s.name,
	pricePerUnit: Number(s.pricePerUnit),
	currency:     asCurrency(s.currency),
	quantity:     Number(s.quantity),
	unit:         s.unit,
	provisionType: s.provisionType ?? 'OWN',
	vatIncluded:  s.vatIncluded,
})

interface EditRentTermsDialogProps {
	property:     Property
	open:         boolean
	onOpenChange: (open: boolean) => void
}

export function EditRentTermsDialog({ property, open, onOpenChange }: EditRentTermsDialogProps) {
	const { t } = useTranslation()
	const accountCurrency = useAccountCurrency()
	const [upsertRentTerms] = useUpsertRentTermsMutation()
	const [upsertUtility]   = useUpsertPropertyUtilityMutation()
	const [deleteUtility]   = useDeletePropertyUtilityMutation()
	const [createService]   = useCreateAdditionalServiceMutation()
	const [updateService]   = useUpdateAdditionalServiceMutation()
	const [deleteService]   = useDeleteAdditionalServiceMutation()
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const rt = property.rentTerms
	const utilitiesByType = useMemo(() => {
		const map: Partial<Record<UtilityType, PropertyUtility>> = {}
		for (const u of property.utilities ?? []) map[u.type] = u
		return map
	}, [property.utilities])
	const initialServiceIds = useMemo(
		() => (property.additionalServices ?? []).map((s) => s.id),
		[property.additionalServices],
	)

	const { form, resetDraft } = usePersistedForm<RentSheetValues>({
		key: `property:rent-sheet:${property.id}`,
		recordVersion: property.updatedAt,
		defaultValues: {
			rentAmount:       rt?.rentAmount    != null ? Number(rt.rentAmount)    : undefined,
			periodicity:      rt?.rentPeriod    ?? 'MONTHLY',
			currency:         asCurrency(rt?.rentCurrency),
			vatIncluded:      rt?.vatIncluded ?? true,
			depositAmount:    rt?.depositAmount != null ? Number(rt.depositAmount) : undefined,
			penaltyPercent:   rt?.penaltyRate   != null ? Number(rt.penaltyRate)   : undefined,
			penaltyGraceDays: rt?.penaltyGraceDays,
			otherUtilitiesPayment: rt?.otherUtilitiesPayment != null ? Number(rt.otherUtilitiesPayment) : undefined,

			// Правка начинается с того, что согласовано, иначе первое же
			// сохранение стёрло бы условия.
			rentUnit:                  rt?.rentUnit,
			agentCommissionPercent:    rt?.agentCommissionPercent != null ? Number(rt.agentCommissionPercent) : undefined,
			leaseKind:                 rt?.leaseKind,
			rentHolidays:              rt?.rentHolidays,
			minLeaseMonths:            rt?.minLeaseMonths,
			utilitiesIncluded:         rt?.utilitiesIncluded,
			operatingExpensesIncluded: rt?.operatingExpensesIncluded,
			depositMonths:             rt?.depositMonths != null ? Number(rt.depositMonths) : undefined,
			utilities: {
				ELECTRICITY: utilityToForm(utilitiesByType.ELECTRICITY),
				WATER:       utilityToForm(utilitiesByType.WATER),
				HEATING:     utilityToForm(utilitiesByType.HEATING),
				WASTE:       utilityToForm(utilitiesByType.WASTE),
			},
			additionalServices: (property.additionalServices ?? []).map(serviceToForm),
		},
		// The same resolver the wizard uses. Nothing renders a raw key today —
		// zod's own messages come from the global error map — but this schema
		// shares its helpers with the wizard's, and the first keyed message added
		// here would have printed as a key.
		resolver: zodResolverI18n(rentSheetSchema) as Resolver<RentSheetValues>,
		mode:     'onBlur',
		active:   open,
	})

	async function onSubmit(values: RentSheetValues) {
		const decStr = (v: number | undefined | null): string | undefined =>
			v != null ? String(v) : undefined

		// The account currency is a default for records that do not have one
		// yet. An existing record keeps the currency its amounts were agreed
		// in — re-stamping today's profile currency here would silently
		// re-denominate saved figures (50 000 RUB became 50 000 EUR).
		const rentCurrency = rt?.rentCurrency ?? accountCurrency
		const depositCurrency = rt?.depositCurrency ?? rentCurrency

		setErrorMsg(undefined)
		try {
			// 1) Базовые условия
			if (values.rentAmount != null && values.rentAmount > 0) {
				await upsertRentTerms({
					id: property.id,
					body: {
						rentAmount:       String(values.rentAmount),
						rentCurrency,
						rentPeriod:       (values.periodicity ?? 'MONTHLY') as RentPeriod,
						vatIncluded:      values.vatIncluded,
						depositAmount:    decStr(values.depositAmount),
						depositCurrency,
						penaltyRate:      decStr(values.penaltyPercent),
						penaltyGraceDays: values.penaltyGraceDays,
						otherUtilitiesPayment: decStr(values.otherUtilitiesPayment),

						rentUnit:                  values.rentUnit,
						agentCommissionPercent:    decStr(values.agentCommissionPercent),
						leaseKind:                 values.leaseKind,
						rentHolidays:              values.rentHolidays,
						minLeaseMonths:            values.minLeaseMonths,
						utilitiesIncluded:         values.utilitiesIncluded,
						operatingExpensesIncluded: values.operatingExpensesIncluded,
						depositMonths:             values.depositMonths != null ? String(values.depositMonths) : undefined,
					},
				}).unwrap()
			}

			// 2) Коммуналка — upsert каждого типа, у которого выбран calculation;
			// если calculation сброшен, а запись уже существовала — удаляем её
			for (const type of UTILITY_TYPES) {
				const u = values.utilities[type]
				if (!u?.calculation) {
					const existing = utilitiesByType[type]
					if (existing) await deleteUtility({ id: property.id, utilityId: existing.id }).unwrap()
					continue
				}
				await upsertUtility({
					id: property.id,
					body: {
						type,
						calculation:      u.calculation,
						paidBy:           u.paidBy ?? 'TENANT',
						hasSeparateMeter: u.hasSeparateMeter,
						meterNumber:      u.meterNumber,
						propertyArea:     decStr(u.propertyArea),
						buildingArea:     decStr(u.buildingArea),
						coefficient:      decStr(u.coefficient),
						ratePerSqm:       decStr(u.ratePerSqm),
						fixedAmount:      decStr(u.fixedAmount),
						currency:         utilitiesByType[type]?.currency ?? accountCurrency,
					},
				}).unwrap()
			}

			// 3) Доп. услуги — diff по id (delete removed, update existing, create new)
			const services = values.additionalServices ?? []
			const currentIds = services.map((s) => s.id).filter(Boolean) as string[]
			const removedIds = initialServiceIds.filter((id) => !currentIds.includes(id))
			for (const serviceId of removedIds) {
				await deleteService({ id: property.id, serviceId }).unwrap()
			}
			const serviceCurrencyById = new Map(
				(property.additionalServices ?? []).map((svc) => [svc.id, svc.currency]),
			)
			for (const s of services) {
				if (!s.name || s.pricePerUnit == null || s.quantity == null || !s.unit) continue
				const body = {
					name:         s.name,
					pricePerUnit: String(s.pricePerUnit),
					currency:     (s.id && serviceCurrencyById.get(s.id)) || accountCurrency,
					quantity:     String(s.quantity),
					unit:         s.unit,
					provisionType: s.provisionType ?? 'OWN',
					vatIncluded:  s.vatIncluded ?? true,
				}
				if (s.id) {
					await updateService({ id: property.id, serviceId: s.id, body }).unwrap()
				} else {
					await createService({ id: property.id, body }).unwrap()
				}
			}

			resetDraft()
			onOpenChange(false)
		} catch (err) {
			console.error('rent-terms save failed:', err)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	const { handleOpenChange, confirmOpen, handleConfirmDiscard, handleCancelDiscard } = useCloseGuard(
		form, onOpenChange, { isLoading: form.formState.isSubmitting, onReset: resetDraft },
	)

	return (
		<>
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<SheetContent side="right" className="!max-w-2xl w-full p-0 flex flex-col">
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full flex-col">
						<header className="flex items-center border-b border-(--color-border) px-6 py-4">
							<h2 className="text-lg font-[500]">
								{t('properties.rentTerms.editTitle', { defaultValue: 'Условия аренды' })}
							</h2>
						</header>

						<div className="flex-1 overflow-y-auto">
							{errorMsg !== undefined && (
								<div className="px-6 pt-4">
									<Alert variant="destructive">
										<AlertCircleIcon className="size-4" />
										<AlertTitle>{t('properties.rentTerms.errors.save')}</AlertTitle>
										{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
									</Alert>
								</div>
							)}
							<Step3RentTerms propertyType={property.type} housingType={property.housingType} />
							<Step4Utilities />
							<Step5AdditionalServices />
						</div>

						<footer className="flex items-center justify-end gap-2 border-t border-(--color-border) px-6 py-4">
							<Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
								{t('common.cancel', { defaultValue: 'Отмена' })}
							</Button>
							<Button type="submit" disabled={form.formState.isSubmitting}>
								{t('common.save', { defaultValue: 'Сохранить' })}
							</Button>
						</footer>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
		<ConfirmDialog
			open={confirmOpen}
			onOpenChange={(v) => { if (!v) handleCancelDiscard() }}
			title={t('common.unsavedChanges.title')}
			description={t('common.unsavedChanges.description')}
			confirmLabel={t('common.unsavedChanges.confirm')}
			cancelLabel={t('common.unsavedChanges.cancel')}
			variant="destructive"
			onConfirm={handleConfirmDiscard}
		/>
		</>
	)
}
